import { useEffect, useRef, useState } from "react";
import axios from "axios";
import InputField from "../../ui/InputField/InputField";
import classes from "../BasicInfo/BasicInfo.module.css";
import { env } from "../../../constants/env";

const OwnerInfo = ({ formData, updateFormData }) => {
  const [ownerSearch, setOwnerSearch] = useState("");
  const [ownerResults, setOwnerResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Debounced search for existing owners
  useEffect(() => {
    if (!ownerSearch.trim()) {
      setOwnerResults([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem("adminToken");
        const res = await axios.get(`${env.apiUrl}/api/owners?search=${ownerSearch}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOwnerResults(res.data.owners || []);
        setShowDropdown(true);
      } catch {
        setOwnerResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [ownerSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSelectOwner = (owner) => {
    updateFormData({
      owner: {
        name: owner.name || "",
        email: owner.email || "",
        address: owner.address || "",
        phoneNumber: owner.phoneNumber || "",
        latitude: owner.latitude || "",
        longitude: owner.longitude || "",
        openingTime: owner.openingTime || "",
        closingTime: owner.closingTime || "",
        emailGreeting: owner.emailGreeting || "",
        pickupInstructions: owner.pickupInstructions || "",
        emailNote: owner.emailNote || "",
      },
    });
    setOwnerSearch(`${owner.name} (${owner.email})`);
    setShowDropdown(false);
  };

  const handleOwnerChange = (e) => {
    const { name, value } = e.target;
    updateFormData({ owner: { ...formData.owner, [name]: value } });
  };

  const handlePointsChange = (e) => {
    updateFormData({ points: parseInt(e.target.value || 0) });
  };

  const handleInsuranceChange = (e) => {
    updateFormData({ insurancePercentage: parseFloat(e.target.value || 0) });
  };

  return (
    <div className={classes.form}>
      <h4>Owner Information</h4>

      {/* Owner search */}
      <div className={classes.group} ref={dropdownRef} style={{ position: "relative" }}>
        <InputField
          labelText="Search existing owner"
          placeholder="Type name or email..."
          value={ownerSearch}
          onChange={(e) => setOwnerSearch(e.target.value)}
          search
        />
        {showDropdown && ownerResults.length > 0 && (
          <ul
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 100,
              background: "#fff",
              border: "1px solid #EAEAEA",
              borderRadius: 8,
              margin: 0,
              padding: 0,
              listStyle: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            {ownerResults.map((owner) => (
              <li
                key={owner._id}
                onClick={() => handleSelectOwner(owner)}
                style={{
                  padding: "10px 16px",
                  cursor: "pointer",
                  fontSize: 14,
                  borderBottom: "1px solid #EAEAEA",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#F5F5F5")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <strong>{owner.name}</strong>
                <span style={{ color: "#888", marginLeft: 8 }}>{owner.email}</span>
              </li>
            ))}
          </ul>
        )}
        {showDropdown && ownerResults.length === 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 100,
              background: "#fff",
              border: "1px solid #EAEAEA",
              borderRadius: 8,
              padding: "10px 16px",
              fontSize: 14,
              color: "#888",
            }}
          >
            No owners found — fill in the fields below to create a new one.
          </div>
        )}
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Owner Name"
          name="name"
          placeholder="Eg. John Doe"
          value={formData.owner?.name || ""}
          required
          onChange={handleOwnerChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Owner Email"
          name="email"
          type="email"
          placeholder="Eg. john@example.com"
          value={formData.owner?.email || ""}
          required
          onChange={handleOwnerChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Points"
          name="points"
          type="number"
          placeholder="Eg. 10"
          value={formData.points || ""}
          min={0}
          onChange={handlePointsChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Insurance Percentage"
          name="insurancePercentage"
          type="number"
          placeholder="Eg. 10"
          min={0}
          max={100}
          value={formData.insurancePercentage || ""}
          onChange={handleInsuranceChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Rental Address"
          name="address"
          placeholder="Eg. 123 Main St, City"
          value={formData.owner?.address || ""}
          onChange={handleOwnerChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Phone Number"
          name="phoneNumber"
          placeholder="Eg. +123456789"
          value={formData.owner?.phoneNumber || ""}
          onChange={handleOwnerChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Latitude"
          name="latitude"
          placeholder="Eg. -15.3875"
          value={formData.owner?.latitude || ""}
          onChange={handleOwnerChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Longitude"
          name="longitude"
          placeholder="Eg. 28.3228"
          value={formData.owner?.longitude || ""}
          onChange={handleOwnerChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Opening Time"
          name="openingTime"
          type="time"
          value={formData.owner?.openingTime || ""}
          onChange={handleOwnerChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Closing Time"
          name="closingTime"
          type="time"
          value={formData.owner?.closingTime || ""}
          onChange={handleOwnerChange}
        />
      </div>

      <h4 style={{ marginTop: 32 }}>Booking Confirmation Email</h4>
      <p style={{ fontSize: 13, color: "var(--text-secondary-color)", marginBottom: 16 }}>
        These fields appear in the customer's "Booking Confirmed!" email. Changes apply to all cars under this owner.
      </p>

      <div className={classes.group}>
        <InputField
          labelText="Message from Provider"
          name="emailGreeting"
          isTextarea
          placeholder="E.g. Welcome! Please ensure you have your ID and booking reference ready on pick-up day."
          value={formData.owner?.emailGreeting || ""}
          onChange={handleOwnerChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Pick-Up Instructions"
          name="pickupInstructions"
          isTextarea
          placeholder="E.g. Please call 30 minutes before arrival. The car will be parked at Gate B."
          value={formData.owner?.pickupInstructions || ""}
          onChange={handleOwnerChange}
        />
      </div>

      <div className={classes.group}>
        <InputField
          labelText="Additional Information"
          name="emailNote"
          isTextarea
          placeholder="E.g. Please return the car with a full tank of fuel."
          value={formData.owner?.emailNote || ""}
          onChange={handleOwnerChange}
        />
      </div>
    </div>
  );
};

export default OwnerInfo;
